import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  TextField
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useAuth } from '../../../../../../hooks/Auth';
import { useModal } from '../../../../../../hooks/Modal';
import { usePickup } from '../../../../../../hooks/Pickup';
import { SearchMyCollectionsContext } from '../..';
import * as S from './styled';

export const ModalCancelPickup = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const [completedCancellation, setCompletedCancellation] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [otherCancelReason, setOtherCancelReason] = useState('');

  const { token } = useAuth();
  const { closeModal, Modal } = useModal();
  const { cancelNoInvoice } = usePickup();

  const {
    cancelReasonList,
    setCancelReasonList,
    pickupIdToModal,
    protocolToModal,
    branchParameterizationToModal,
    refreshSearch,
    openOtherCancelToFill
  } = useContext(SearchMyCollectionsContext);

  const cancelPickupSchema = Yup.object().shape({
    cancelReason: Yup.string().required('Motivo obrigatório'),
    otherCancelReason: Yup.string().test({
      test: (value, { parent }) => {
        if (
          cancelReasonList?.find(
            (element) => element.cancelId?.toString() === parent.cancelReason
          )?.cancelDescription === 'OUTROS MOTIVOS' &&
          !value &&
          openOtherCancelToFill
        ) {
          return false;
        }
        return true;
      },
      message: 'Motivo obrigatório'
    })
  });

  const {
    handleSubmit,
    control,
    setError,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(cancelPickupSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const onSubmit = (event) => {
    event.preventDefault();
    setConfirmCancellation(true);
  };

  const handleCancelInvoice = async () => {
    try {
      setIsSubmitting(true);

      const params = {
        pickupId: pickupIdToModal,
        parameterizationId: branchParameterizationToModal,
        cancelReasonId: cancelReason,
        otherCancelReason
      };

      const response = await cancelNoInvoice({ data: params, token });

      if (response.status === 200) {
        setConfirmCancellation(false);
        setCompletedCancellation(true);
        refreshSearch();
      } else {
        throw new Error();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      setConfirmCancellation(false);
      setCompletedCancellation(false);

      toast(
        <Toast
          title="Atenção!"
          content="Falha ao cancelar coleta, tente novamente mais tarde."
          type="error"
        />,
        options
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal>
      <form onSubmit={onSubmit}>
        {!confirmCancellation && !completedCancellation && !confirmClose && (
          <Stack spacing={2}>
            <S.ModalTitle>Cancelar Coleta</S.ModalTitle>
            <S.SectionTitle>Qual o motivo deste cancelamento?</S.SectionTitle>
            <Controller
              name="cancelReason"
              defaultValue=""
              control={control}
              render={({ field }) => (
                <FormControl
                  style={{ width: 360 }}
                  error={!!errors.cancelReason}
                >
                  <InputLabel id="cancelReason">
                    Selecione o motivo do cancelamento:
                  </InputLabel>
                  <Select
                    id="cancelReason"
                    label="Selecione o motivo do cancelamento:"
                    {...field}
                    value={(field.value = cancelReason)}
                    onChange={(event) => {
                      setCancelReason(event.target.value);
                      field.onChange(event.target.value);
                    }}
                    error={!!errors.cancelReason}
                  >
                    {cancelReasonList?.map(
                      ({ cancelDescription, cancelId }) => (
                        <MenuItem key={cancelId} value={cancelId}>
                          {cancelDescription}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.cancelReason?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />

            {cancelReasonList?.find(
              (element) => element.cancelId === cancelReason
            )?.cancelDescription === 'OUTROS MOTIVOS' &&
              openOtherCancelToFill && (
                <Controller
                  name="otherCancelReason"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      name="otherCancelReason"
                      id="otherCancelReason"
                      label="Motivo"
                      {...field}
                      onChange={(e) => {
                        setOtherCancelReason(e.target.value);
                        field.onChange(e.target.value);
                      }}
                      style={{ width: '100%' }}
                      error={!!errors.otherCancelReason}
                      helperText={errors.otherCancelReason?.message}
                    />
                  )}
                />
                // eslint-disable-next-line prettier/prettier
            )}

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={() => {
                  setConfirmClose(true);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={
                  cancelReasonList?.find(
                    (element) => element.cancelId === cancelReason
                  )?.cancelDescription === 'OUTROS MOTIVOS' &&
                  openOtherCancelToFill
                    ? !otherCancelReason
                    : !cancelReason
                }
                variant="contained"
              >
                Confirmar
              </Button>
            </Box>
          </Stack>
        )}

        {confirmCancellation && (
          <Stack spacing={2}>
            <S.ModalTitle>Cancelar Coleta</S.ModalTitle>
            <S.ModalText>
              A coleta de Protocolo {protocolToModal} será cancelada.
            </S.ModalText>
            <S.ModalText>Deseja Continuar?</S.ModalText>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="attention"
                onClick={() => {
                  setCancelReasonList([]);
                  closeModal();
                }}
                disabled={isSubmitting}
              >
                Não
              </Button>
              <LoadingButton
                type="button"
                variant="contained"
                onClick={() => {
                  handleCancelInvoice();
                }}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Sim
              </LoadingButton>
            </Box>
          </Stack>
        )}

        {completedCancellation && (
          <Stack spacing={2}>
            <S.ModalTitle>Cancelamento Concluído</S.ModalTitle>
            <S.ModalText>
              Sua solicitação para cancelamento da coleta de Protocolo{' '}
              {protocolToModal} foi realizada com sucesso.
              <br />
              <br />
              Em breve receberá um e-mail!
            </S.ModalText>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  setCancelReasonList([]);
                  closeModal();
                }}
              >
                Ok
              </Button>
            </Box>
          </Stack>
        )}

        {confirmClose && (
          <Stack spacing={2}>
            <S.ModalTitle>Atenção</S.ModalTitle>

            <S.ModalText>
              Deseja cancelar a solicitação de cancelamento?
            </S.ModalText>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="attention"
                onClick={() => setConfirmClose(false)}
              >
                Não
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={() => {
                  setCancelReasonList([]);
                  closeModal();
                }}
              >
                Sim
              </Button>
            </Box>
          </Stack>
        )}
      </form>
    </Modal>
  );
};
